import { brand, formatMoney } from '../brand/brand';
import type { Deal } from '../api/types';

/**
 * The questions a buyer has BEFORE paying, answered from this deal's own data.
 *
 * Every answer is derived from a field the voucher is actually issued against —
 * the expiry date, the branch list, the per-customer limit — rather than typed
 * into a content field somewhere. That is the whole point: a hand-written FAQ
 * saying "valid for 12 months" goes stale the moment someone edits the deal, and
 * nobody notices until a customer is turned away at a counter holding a voucher
 * our own page told them was valid.
 *
 * <details> rather than a custom accordion. It is keyboard operable, announced
 * correctly, and searchable by the browser's find-in-page with no JavaScript at
 * all — three things hand-rolled accordions usually get wrong.
 */
export function DealFaq({ deal }: { deal: Deal }) {
  const expires = new Date(deal.validTo);

  const where = deal.locations.length === 0
    ? `Any ${deal.businessName} location. If they open a new one before your voucher expires, it works there too.`
    : `These locations only: ${deal.locations.join(', ')}. It cannot be used at other ${deal.businessName} branches.`;

  const howMany = deal.maxPerUser === null
    ? 'As many as you like, while stock lasts. Choose the quantity before you pay.'
    : `Up to ${deal.maxPerUser} per customer. Buying for someone else counts against your own limit, because the voucher is issued to your account.`;

  const items: { q: string; a: string }[] = [
    {
      q: 'What exactly am I buying?',
      a: `${formatMoney(deal.priceCents)} buys ${formatMoney(deal.faceValueCents)} of credit at `
        + `${deal.businessName} — a saving of ${formatMoney(deal.savingsCents)}. `
        + 'The credit works like cash with them; it is not a discount off a particular item.',
    },
    { q: 'Where can I use it?', a: where },
    {
      q: 'Do I have to spend it all at once?',
      a: 'No. The voucher holds a balance. Spend part of it and the rest stays on the '
        + 'voucher for next time — your wallet shows what is left after every visit.',
    },
    {
      q: 'When does it expire?',
      a: `${expires.toLocaleDateString(brand.language, { dateStyle: 'long' })}. `
        + 'We email you before it runs out. Any balance left after that date is no longer spendable.',
    },
    { q: 'How many can I buy?', a: howMany },
    {
      q: 'How do I use it at the counter?',
      a: 'Open the voucher on your phone and show the code. Nothing to print, and no app '
        + 'to install. Treat the code like cash — anyone who has it can spend it.',
    },
    {
      q: 'Can I get a refund?',
      a: 'An unused voucher can be refunded on request. Once any part of the balance has '
        + `been spent it cannot be. If ${deal.businessName} will not honour a valid voucher, `
        + 'tell us and we will deal with it.',
    },
  ];

  return (
    <section className="stack" style={{ gap: 10 }}>
      <h2>Questions</h2>

      <div className="faq">
        {items.map((item) => (
          <details key={item.q}>
            <summary>{item.q}</summary>
            <p className="muted">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
